// Fase 14A — I18n layer (punto 67): chiavi stabili, fallback IT, warning su chiavi
// mancanti (punto 71), formattazione numeri/date per locale via Intl (punti 73-74).
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import it from "@/lib/translations/it";
import en from "@/lib/translations/en";

const DICTS = { it, en };
const LOCALES = { it: "it-IT", en: "en-GB", fr: "fr-FR", de: "de-DE", es: "es-ES", pt: "pt-PT" };

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const { user } = useAuth();
  const [language, setLanguageState] = useState("it");
  const [languages, setLanguages] = useState([]);
  const [explicit, setExplicit] = useState(true);

  useEffect(() => {
    api.get("/languages").then(({ data }) => setLanguages(data.languages || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) {
      setLanguageState("it");
      setExplicit(true);
      return;
    }
    api.get("/me/language").then(({ data }) => {
      setLanguageState(data.language || "it");
      setExplicit(!!data.explicit);
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const t = useCallback((key, vars) => {
    let s = DICTS[language] ? DICTS[language][key] : undefined;
    if (s == null && language !== "it") {
      console.warn(`[i18n] missing '${key}' for '${language}', fallback it`);
      s = DICTS.it[key];
    }
    if (s == null) {
      console.warn(`[i18n] missing key '${key}'`);
      return "";
    }
    if (vars) {
      for (const [k, v] of Object.entries(vars)) {
        s = s.replaceAll(`{${k}}`, String(v));
      }
    }
    return s;
  }, [language]);

  const setLanguage = useCallback(async (code, scope = "ALL") => {
    await api.put("/me/language", { language: code, scope });
    if (scope !== "EVA") setLanguageState(code);
    setExplicit(true);
  }, []);

  const locale = LOCALES[language] || "it-IT";
  const fmtNum = useCallback((n, opts) => (
    n === null || n === undefined || Number.isNaN(Number(n))
      ? "—"
      : new Intl.NumberFormat(locale, opts).format(n)
  ), [locale]);
  const fmtDate = useCallback((d) => {
    if (!d) return "—";
    const dt = typeof d === "string" ? new Date(d) : d;
    if (Number.isNaN(dt.getTime())) return "—";
    return new Intl.DateTimeFormat(locale, { day: "2-digit", month: "2-digit", year: "numeric" }).format(dt);
  }, [locale]);

  const value = useMemo(() => ({
    t, language, languages, setLanguage, explicit, setExplicit,
    fmtNum, fmtDate, locale,
  }), [t, language, languages, setLanguage, explicit, fmtNum, fmtDate, locale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}
